import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { filter, take } from 'rxjs/operators';
import { VoucherService } from '../../services/voucher.service';
import { PermissionService } from '../../services/permission.service';
import { 
  PendingRedemptionCustomerDto, 
  PartnerZoneDto, 
  SaveFollowUpDto, 
  PendingRedemptionFilterParams 
} from '../../models/voucher.model';

@Component({
    selector: 'app-pending-redemptions',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './pending-redemptions.component.html',
    styleUrl: './pending-redemptions.component.css',
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: 0, opacity: 0, overflow: 'hidden' }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ height: 0, opacity: 0, overflow: 'hidden' }))
            ])
        ]),
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('150ms ease-in', style({ opacity: 0 }))
            ])
        ])
    ]
})
export class PendingRedemptionsComponent implements OnInit {
    customers: PendingRedemptionCustomerDto[] = [];
    zones: (PartnerZoneDto & { selected: boolean })[] = [];
    isLoading: boolean = false;
    errorMessage: string = '';
    
    // Filters and sorting
    filters: PendingRedemptionFilterParams = {
        customerName: undefined,
        customerPhoneNumber: undefined,
        zoneIds: undefined,
        status: undefined,
        sortBy: 'ExpiryDate',
        sortDescending: false,
        pageIndex: 0,
        pageSize: 20
    };

    // UI state
    showFilters: boolean = true;
    showZoneDropdown: boolean = false;
    
    // Pagination state
    totalCount: number = 0;
    totalPages: number = 0;
    currentPage: number = 1;
    
    // Follow-up Modal state
    showFollowUpModal: boolean = false;
    selectedCustomer: PendingRedemptionCustomerDto | null = null;
    newStatus: string = 'Contacted';
    newRemarks: string = '';
    isSavingFollowUp: boolean = false;
    saveError: string = '';
    
    // Follow-up status options
    statusOptions = ['Not Contacted', 'Contacted', 'No Answer', 'Follow-up Required'];
    
    // Expose Math to template
    Math = Math;

    constructor(
        private voucherService: VoucherService,
        private permissionService: PermissionService,
        private elementRef: ElementRef
    ) { }

    ngOnInit(): void {
        this.permissionService.permissions$.pipe(
            filter(permissions => permissions.length > 0),
            take(1)
        ).subscribe(() => {
            this.loadZones();
            this.loadCustomers();
        });
    }

    /**
     * Fetch all available partner zones
     */
    loadZones(): void {
        this.voucherService.getPartnerZones().subscribe({
            next: (data) => {
                this.zones = data.map(z => ({ ...z, selected: false }));
            },
            error: (err) => {
                console.error('Error loading partner zones:', err);
            }
        });
    }

    /**
     * Load pending redemption customers with current filter & sort parameters
     */
    loadCustomers(): void {
        this.isLoading = true;
        this.errorMessage = '';

        this.voucherService.getPendingRedemptionCustomers(this.filters).subscribe({
            next: (response) => {
                this.customers = response.items;
                this.totalCount = response.totalCount;
                this.currentPage = response.pageNumber + 1;
                this.totalPages = Math.ceil(response.totalCount / response.pageSize);
                this.isLoading = false;
            },
            error: (error) => {
                this.isLoading = false;
                this.errorMessage = error.error?.message || 'Failed to load pending redemption customers. Please try again.';
                console.error('Error loading pending redemption customers:', error);
            }
        });
    }

    /**
     * Apply filters
     */
    applyFilters(): void {
        // Build comma-separated Zone IDs from checklist
        const selectedZoneIds = this.zones.filter(z => z.selected).map(z => z.id).join(',');
        this.filters.zoneIds = selectedZoneIds ? selectedZoneIds : undefined;
        
        this.filters.pageIndex = 0; // Reset pagination
        this.loadCustomers();
    }

    /**
     * Clear filters
     */
    clearFilters(): void {
        this.zones.forEach(z => z.selected = false);
        this.filters = {
            customerName: undefined,
            customerPhoneNumber: undefined,
            zoneIds: undefined,
            status: undefined,
            sortBy: 'ExpiryDate',
            sortDescending: false,
            pageIndex: 0,
            pageSize: 20
        };
        this.loadCustomers();
    }

    /**
     * Sorting toggle
     */
    toggleSort(column: string): void {
        if (this.filters.sortBy === column) {
            this.filters.sortDescending = !this.filters.sortDescending;
        } else {
            this.filters.sortBy = column;
            this.filters.sortDescending = false;
        }
        this.filters.pageIndex = 0;
        this.loadCustomers();
    }

    /**
     * Get sorting icon classes
     */
    getSortIcon(column: string): string {
        if (this.filters.sortBy !== column) {
            return 'fas fa-sort text-gray-300 ml-1';
        }
        return this.filters.sortDescending ? 'fas fa-sort-down text-green-600 ml-1' : 'fas fa-sort-up text-green-600 ml-1';
    }

    /**
     * Toggle filter panel visibility
     */
    toggleFilters(): void {
        this.showFilters = !this.showFilters;
    }

    /**
     * Toggle custom multi-select zone dropdown
     */
    toggleZoneDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.showZoneDropdown = !this.showZoneDropdown;
    }

    /**
     * Close zone dropdown when clicking outside
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.zone-dropdown-container')) {
            this.showZoneDropdown = false;
        }
    }

    /**
     * Get label showing number of selected zones
     */
    getSelectedZonesLabel(): string {
        const count = this.zones.filter(z => z.selected).length;
        if (count === 0) return 'All Zones';
        if (count === 1) return this.zones.find(z => z.selected)!.name;
        return `${count} Zones Selected`;
    }

    /**
     * Format dates safely for display
     */
    formatDate(dateString?: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    /**
     * Format date & time for display in follow-up history
     */
    formatDateTime(dateString?: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Open follow-up history/interaction modal
     */
    openFollowUpModal(customer: PendingRedemptionCustomerDto): void {
        this.selectedCustomer = customer;
        this.newStatus = customer.followUpStatus || 'Not Contacted';
        this.newRemarks = '';
        this.saveError = '';
        this.showFollowUpModal = true;
    }

    /**
     * Close follow-up modal
     */
    closeFollowUpModal(): void {
        this.showFollowUpModal = false;
        this.selectedCustomer = null;
    }

    /**
     * Submit new follow-up outreach status and remarks
     */
    submitFollowUp(): void {
        if (!this.selectedCustomer) return;
        if (!this.newRemarks.trim()) {
            this.saveError = 'Please enter outreach remarks.';
            return;
        }
        
        this.isSavingFollowUp = true;
        this.saveError = '';
        
        const dto: SaveFollowUpDto = {
            customerId: this.selectedCustomer.customerId,
            status: this.newStatus,
            remarks: this.newRemarks.trim()
        };
        
        this.voucherService.saveFollowUp(dto).subscribe({
            next: (savedFollowUp) => {
                this.isSavingFollowUp = false;
                this.closeFollowUpModal();
                // Reload list to pick up the updated follow-up status, date, remarks, and history timeline log
                this.loadCustomers();
            },
            error: (err) => {
                this.isSavingFollowUp = false;
                this.saveError = err.error?.message || 'Failed to record follow-up. Please try again.';
                console.error('Error saving follow-up:', err);
            }
        });
    }

    /**
     * Get follow-up status badge class
     */
    getStatusClass(status: string): string {
        switch (status) {
            case 'Contacted':
                return 'status-contacted';
            case 'No Answer':
                return 'status-no-answer';
            case 'Follow-up Required':
                return 'status-follow-up-req';
            case 'Not Contacted':
            default:
                return 'status-not-contacted';
        }
    }

    /**
     * Checking if filter criteria are actively selected
     */
    hasActiveFilters(): boolean {
        return !!(
            this.filters.customerName ||
            this.filters.customerPhoneNumber ||
            this.filters.status ||
            this.zones.some(z => z.selected)
        );
    }

    /**
     * Pagination navigation helper
     */
    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.filters.pageIndex = page - 1;
        this.loadCustomers();
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.goToPage(this.currentPage + 1);
        }
    }

    previousPage(): void {
        if (this.currentPage > 1) {
            this.goToPage(this.currentPage - 1);
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    }

    /**
     * Handle page size changes
     */
    onPageSizeChange(size: number): void {
        this.filters.pageSize = size;
        this.filters.pageIndex = 0; // Reset to first page
        this.loadCustomers();
    }

    /**
     * Determine the expiry date color-coding class
     * Red if past, Yellow if within 1 month, Green otherwise
     */
    getExpiryClass(dateString?: string): string {
        if (!dateString) return '';
        
        const expiryDate = new Date(dateString);
        const now = new Date();
        
        // Reset time parts to do strict date-only comparisons
        const expiryDateDate = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
        const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (expiryDateDate < nowDate) {
            return 'expiry-past'; // past
        }

        // Calculate difference in days
        const diffTime = expiryDateDate.getTime() - nowDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays <= 30) {
            return 'expiry-soon'; // within 1 month (30 days)
        }

        return 'expiry-future'; // green
    }
}
